// AIMS PWA - offline-mode API client.
//
// Thin wrappers over the Phase 1 backend endpoints (AIMS-Offline-Inspection.md
// §2) plus the reused template/type-map reads. Kept separate from UI so the
// take-offline orchestration and the (Phase 4) sync engine share one call layer.
//
// Auth uses the standard username + bToken headers (house style). The durable
// bToken is the offline credential; refreshJwt() mints a short-lived JWT when a
// live one is needed.

import { baseUrl } from '../shared';

function authHeaders(extra = {}) {
  return {
    username: localStorage.getItem('username'),
    bToken: localStorage.getItem('bToken'),
    ...extra,
  };
}

async function asJson(res) {
  if (!res.ok) {
    const err = new Error('Request failed: ' + res.status);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

// bToken -> fresh 5-min JWT (called first on every sync).
export async function refreshJwt() {
  const res = await fetch(baseUrl + 'token/refresh', { method: 'POST', headers: authHeaders() });
  return asJson(res);
}

// Lease the visit's assets + return the full cacheable pool.
export async function offlineCheckout(visitId, deviceLabel) {
  const res = await fetch(baseUrl + 'schedule/visits/' + visitId + '/offline-checkout', {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ deviceLabel: deviceLabel || null }),
  });
  return asJson(res);
}

// Authoritative delta for the reconcile loop (Phase 4).
export async function offlineSync(visitId) {
  const res = await fetch(baseUrl + 'schedule/visits/' + visitId + '/offline-sync', {
    method: 'GET', headers: authHeaders(),
  });
  return asJson(res);
}

// Release the lease.
export async function offlineRelease(visitId) {
  const res = await fetch(baseUrl + 'schedule/visits/' + visitId + '/offline-release', {
    method: 'POST', headers: authHeaders(),
  });
  return asJson(res);
}

// Who-has-it-out detail behind the asset-view "out for field inspection" chip.
export async function fetchOfflineCheckoutDetail(visitId) {
  const res = await fetch(baseUrl + 'schedule/visits/' + visitId + '/offline-checkout-detail', {
    method: 'GET', headers: authHeaders(),
  });
  return asJson(res);
}

// HQ force-clear of an offline checkout (schedule managers only). Pass a
// checkoutId to clear one row, or nothing to clear all open rows.
export async function offlineClear(visitId, checkoutId) {
  const res = await fetch(baseUrl + 'schedule/visits/' + visitId + '/offline-clear', {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ checkoutId: checkoutId || null }),
  });
  return asJson(res);
}

// Idempotent batch push of completed inspections (Phase 4).
export async function pushResultsBatch(items) {
  const res = await fetch(baseUrl + 'inspection/results/batch', {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ items }),
  });
  return asJson(res);
}

// Reused reads (no backend change): checklist template + item→auditType map.
export async function fetchChecklistTemplate(auditType) {
  const res = await fetch(baseUrl + 'inspection/checklist/' + encodeURIComponent(auditType), {
    method: 'GET', headers: authHeaders(),
  });
  if (res.status === 404) return []; // no checklist configured - valid (no-checklist inspection)
  return asJson(res);
}

export async function fetchTypeMap() {
  const res = await fetch(baseUrl + 'workshop/settings/inspection-type-map', {
    method: 'GET', headers: authHeaders(),
  });
  return asJson(res);
}
